"use client"

import { memo, useRef, useState, useEffect, useCallback } from "react"
import Image from "next/image"
import Link from "next/link"
import { useParams } from "next/navigation"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { track } from "@/lib/analytics"
import { CarouselNav } from "@/components/ui/carousel-nav"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
import { blobMediaUrl } from "@/lib/blob-url"

/** Type 1: qualitative result with colored dot (MRI, screening) */
interface BiomarkerDot {
  type: "dot"
  label: string
  value: string
  status: string
}

/** Type 2: quantitative result with value/unit/range/status pill (blood, CT) */
interface BiomarkerPill {
  type: "pill"
  label: string
  value: string
  unit: string
  range: string
  status: string
}

/** Type 3: member-savings label, used by the membership page where the
   biomarker overlay is repurposed to highlight per-card savings. */
interface BiomarkerSavings {
  type: "savings"
  label: string
  amount: string
}

export interface ServiceCardData {
  title: string
  description: string
  image: string
  /** Optional object-position override (e.g. "object-[70%_center]") for images
      whose subject sits off-centre and would otherwise be cropped. */
  imageClassName?: string
  /** Add a subtle dark wash to the top of the card so the light biomarker
      chip stays legible over a bright hero image (e.g. the prostate card). */
  topWash?: boolean
  path: string
  biomarker?: BiomarkerDot | BiomarkerPill | BiomarkerSavings
  /**
   * CMS-driven per-country availability for this card, joined by slug from the
   * `services` control plane (see `lib/services-overlay.ts`). It is the SOLE
   * driver of the status pill — there is no hardcoded fallback rule anymore.
   * Absent (no CMS config, or a slug with no CMS row) means the card renders
   * UNGATED (no pill); the loader fires Sentry when the whole config is empty so
   * that degraded state is never silent. `hidden` cards are filtered out by the
   * overlay before they reach here, and `available` is never set (it's the
   * no-pill default), so in practice this is `coming_soon` | `waitlist` |
   * `not_available` when set. `coming_soon` / `waitlist` share the frosted
   * "coming soon" pill — the difference between them is a booking-flow
   * detail, not something a browsing card should spell out. `not_available`
   * reads "Not available": it is the opposite claim, not a softer one.
   */
  marketStatus?: "coming_soon" | "waitlist" | "not_available"
  /**
   * The card path whose CMS control-plane row this card falls back to when its
   * own `path` has none. Set when a market swaps one card for another in place
   * (DE: the Vorsorge-Starter stands in for the Advanced blood test), so the
   * swapped card keeps the slot the CMS gives the card it replaces instead of
   * sorting after every configured card. Its own row wins as soon as it exists.
   */
  controlPlaneFallbackPath?: string
}

export interface ServicesCarouselProps {
  items: ServiceCardData[]
  className?: string
  learnMoreLabel?: string
  disclaimerLabel?: string
  /**
   * Optional content rendered at the start (left) of the toolbar row that
   * also holds the prev/next chevrons. Overrides the disclaimer slot when
   * provided. Used by the membership page to inline a "See all member
   * prices" toggle alongside the carousel chevrons.
   */
  toolbarLeading?: React.ReactNode
}

export const DEFAULT_SERVICES: ServiceCardData[] = [
  {
    title: "Full-body MRI",
    description:
      "A full-body scan offering detailed insights into major organs for preventive health.",
    image: blobMediaUrl("/images/homepage/service-mri.jpeg"),
    path: "/tests/full-body-mri",
    biomarker: {
      type: "dot",
      label: "Spine",
      value: "Normal",
      status: "1 Minor finding",
    },
  },
  {
    title: "Advanced blood tests",
    description:
      "Test 80+ biomarkers, including cholesterol and glucose, for a full health overview.",
    image: blobMediaUrl("/images/homepage/service-blood.jpeg"),
    path: "/tests/advanced-blood-test",
    biomarker: {
      type: "dot",
      label: "Cardiovascular health",
      value: "1.9% Heart risk score",
      status: "Low",
    },
  },
  {
    title: "Body composition",
    description:
      "Measure muscle & fat distribution and its impact on your metabolism.",
    image: blobMediaUrl("/images/homepage/service-report.jpeg"),
    path: "/tests/body-composition",
    biomarker: {
      type: "pill",
      label: "Total abdominal fat",
      value: "2.4",
      unit: "L",
      range: "< 10",
      status: "Optimal",
    },
  },
  {
    title: "DEXA scan",
    description:
      "Measure bone density and osteoporosis risk with a low-dose scan.",
    image: blobMediaUrl("/images/addons/dexa-hero.jpeg"),
    imageClassName: "object-[90%_center]",
    path: "/tests/dexa-scan",
    biomarker: {
      type: "dot",
      label: "T-score",
      value: "-0.8",
      status: "Normal range",
    },
  },
  {
    title: "Gender health",
    description:
      "Targeted MRI of the prostate or ovaries and uterus for earlier, clearer gender-specific screening.",
    image: blobMediaUrl("/images/addons/gender-health.jpg"),
    path: "/tests/gender-health",
    biomarker: {
      type: "dot",
      label: "Screening result",
      value: "No findings",
      status: "Follow-up if indicated",
    },
  },
  {
    title: "Prostate MRI",
    description:
      "A focused, contrast-free MRI of the prostate with a clear PI-RADS risk score.",
    image: blobMediaUrl("/images/gender-health/prostate-mri-hero.jpg"),
    topWash: true,
    path: "/tests/prostate-mri",
    biomarker: {
      type: "dot",
      label: "PI-RADS",
      value: "2",
      status: "Low risk",
    },
  },
  {
    title: "Pelvic MRI",
    description:
      "High-resolution, radiation-free imaging of the uterus, endometrium and ovaries.",
    image: blobMediaUrl("/images/gender-health/ovaries-mri-hero.jpg"),
    path: "/tests/pelvic-mri",
    biomarker: {
      type: "dot",
      label: "Screening result",
      value: "No findings",
      status: "Follow-up if indicated",
    },
  },
  {
    title: "Brain volumetry",
    description:
      "Map your brain\u2019s structure and volume to evaluate your cognitive health.",
    image: blobMediaUrl("/images/homepage/service-brain.jpeg"),
    path: "/tests/brain-volumetry",
    biomarker: {
      type: "pill",
      label: "Frontal lobe volume",
      value: "80.9",
      unit: "mL",
      range: "76–96.4",
      status: "Normal",
    },
  },
  {
    title: "Omega-3 & 6 blood test",
    description:
      "Measure 16 biomarkers to know if your Omega intake meets your body\u2019s needs.",
    image: blobMediaUrl("/images/homepage/service-omega.jpeg"),
    path: "/tests/omega-blood-test",
    biomarker: {
      type: "pill",
      label: "Omega-3 index",
      value: "8.7",
      unit: "%",
      range: "≥ 8",
      status: "Optimal",
    },
  },
  {
    title: "Mamma MRI",
    description:
      "Radiation-free imaging for early and precise assessment of your breast health.",
    image: blobMediaUrl("/images/homepage/service-mamma.jpeg"),
    path: "/tests/mamma-mri",
    biomarker: {
      type: "dot",
      label: "Screening results",
      value: "BI-RADS 1",
      status: "No findings",
    },
  },
  {
    title: "Hormone test for men",
    description:
      "Test 7 key biomarkers like testosterone, estradiol and DHEA-S.",
    image: blobMediaUrl("/images/homepage/service-hormone-men.jpeg"),
    path: "/tests/hormone-blood-test-men",
    biomarker: {
      type: "dot",
      label: "Free testosterone",
      value: "Low",
      status: "Low – see a doctor",
    },
  },
  {
    title: "Hormone test for women",
    description:
      "Test 8 hormone markers for women, including estradiol, progesterone, LH and FSH.",
    image: blobMediaUrl("/images/homepage/service-hormone-women.jpeg"),
    path: "/tests/hormone-blood-test-women",
    biomarker: {
      type: "pill",
      label: "FSH",
      value: "9.8",
      unit: "U/L",
      range: "3.5-12.5",
      status: "Within range (follicular phase)",
    },
  },
  {
    title: "Calcium score",
    description:
      "A CT scan that measures calcium buildup in your coronary arteries.",
    image: blobMediaUrl("/images/homepage/service-calcium.jpeg"),
    path: "/tests/calcium-score",
    biomarker: {
      type: "pill",
      label: "Agatston score",
      value: "7",
      unit: "AU",
      range: "Heart health",
      status: "Normal range",
    },
  },
  {
    title: "Essential-B-vitamins blood test",
    description:
      "Check B12 and folate (B9) levels to reveal if deficiencies are behind fatigue or low focus.",
    image: blobMediaUrl("/images/homepage/service-bvitamins.jpeg"),
    path: "/tests/essential-b-vitamins-test",
    biomarker: {
      type: "pill",
      label: "Folate",
      value: "18",
      unit: "nmol/L",
      range: "Metabolism",
      status: "Within range",
    },
  },
  {
    title: "Lipoprotein (a)",
    description:
      "A single inherited blood marker that can quietly raise your risk of heart attack and stroke.",
    image: blobMediaUrl("/images/marketing/lpa-hero-train.jpeg"),
    path: "/tests/lpa",
    biomarker: {
      type: "dot",
      label: "Lp(a)",
      value: "32 nmol/L",
      status: "Desirable",
    },
  },
  {
    title: "Cortisol",
    description:
      "Your body’s primary stress hormone, measured at its natural morning peak.",
    image: blobMediaUrl("/images/marketing/cortisol-hero-kitchen.jpeg"),
    path: "/tests/cortisol-blood-test",
    biomarker: {
      type: "dot",
      label: "Cortisol (morning)",
      value: "480 nmol/L",
      status: "Healthy morning level",
    },
  },
  {
    // NEEDS CLINICAL / HWG SIGN-OFF: screening-context copy only (no diagnostic
    // claim); the illustrative ng/mL-equivalent value below is for display.
    title: "PSA blood test",
    description:
      "Add the prostate-specific antigen (PSA) blood marker to follow your prostate health over time.",
    image: blobMediaUrl("/images/marketing/psa-hero-window.jpeg"),
    // Bright hero image + light biomarker chip → dark top wash for legibility
    // (the "prostate card" case called out on `topWash` above).
    topWash: true,
    path: "/tests/psa-blood-test",
    biomarker: {
      type: "dot",
      label: "PSA",
      value: "1.2 µg/L",
      status: "Within range",
    },
  },
]

function ServiceCard({
  item,
  learnMoreLabel,
}: {
  item: ServiceCardData
  learnMoreLabel?: string
}) {
  const t = useTranslations("ui")
  const params = useParams()
  const country = (params?.country as string) || "ch"
  const locale = (params?.locale as string) || "en"
  const localizedHref = `/${country}/${locale}${item.path}`
  // Status pill — driven SOLELY by the CMS `services` control plane via
  // `card.marketStatus` (set by `applyServicesConfig`, joined by slug). There is
  // no hardcoded fallback rule: when the CMS config is empty/failed the card has
  // no `marketStatus` and renders ungated (no pill), and the loader fires Sentry
  // so that degraded state is never silent.
  const marketStatus = item.marketStatus ?? null
  // `not_available` greys the whole card (pill in the info-icon grey, photo
  // desaturated, text dimmed) so it reads as inert next to "coming soon".
  const muted = marketStatus === "not_available"

  return (
    <Link
      href={localizedHref}
      className="group relative block min-h-[420px] w-[300px] flex-shrink-0 cursor-pointer snap-start overflow-hidden rounded-xl bg-neutral-100 md:min-h-[456px] md:w-[344px]"
    >
      {/* Status pill — TWO labels, one treatment. `coming_soon` / `waitlist`
          both read "coming soon" (the split between them is a booking-flow
          detail, not a browsing one); `not_available` says so, because a scan
          the market cannot deliver is the opposite claim and collapsing it
          into "coming soon" (2026-07-29, reverted) promised something we then
          have to decline. Both keep the frosted `glass` variant — the solid
          "Not available" ActionBadge that predated that change clashed with
          the biomarker chips beside it, and that fix is worth keeping.
          Top-right so it clears the biomarker overlay (top-left); stays put on
          hover. */}
      {marketStatus ? (
        <ComingSoonPill
          label={t(
            marketStatus === "not_available" ? "notAvailable" : "comingSoon"
          )}
          variant="glass"
          className={cn(
            "absolute top-3 right-3 z-20 md:top-4 md:right-4",
            muted && "bg-neutral-100/90 text-neutral-500"
          )}
        />
      ) : null}
      {/* Image + gradient + bottom text — slides up on hover */}
      <div className="absolute inset-0 overflow-hidden transition-transform duration-300 ease-out group-hover:-translate-y-[43px]">
        <Image
          src={item.image}
          alt={item.title}
          fill
          sizes="(min-width: 768px) 344px, 300px"
          className={cn(
            "rounded-xl object-cover",
            muted && "opacity-70 grayscale",
            item.imageClassName
          )}
        />
        <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

        {/* Title + description */}
        <div className="absolute right-0 bottom-0 left-0 p-5 md:p-6">
          <h3
            className={cn(
              "text-xl font-medium md:text-2xl",
              muted ? "text-white/70" : "text-white"
            )}
          >
            {item.title}
          </h3>
          <p
            className={cn(
              "mt-1 text-sm",
              muted ? "text-white/50" : "text-white/70"
            )}
          >
            {item.description}
          </p>
        </div>
      </div>

      {/* Subtle top wash to keep the light biomarker chip legible over a
          bright hero image. Sits above the photo but below the chip, and
          stays put on hover (outside the sliding image layer). */}
      {item.topWash && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-2/5 bg-gradient-to-b from-black/35 to-transparent" />
      )}

      {/* Glass panel biomarker overlay — stays in place on hover. The
          savings variant flips to a lighter glass + dark text since it's
          read as a value, not status. */}
      {item.biomarker && (
        <div
          className={cn(
            "absolute top-3 left-3 z-10 rounded-md px-3 py-2 backdrop-blur-[5px] md:top-4 md:left-4",
            muted && "opacity-60",
            item.biomarker.type === "savings"
              ? "bg-white/30"
              : "bg-white/[0.07]"
          )}
        >
          <span
            className={cn(
              "block text-sm font-normal",
              item.biomarker.type === "savings"
                ? "text-neutral-700"
                : "text-white/70"
            )}
          >
            {item.biomarker.label}
          </span>
          {item.biomarker.type === "dot" ? (
            <>
              <span className="block text-lg font-medium text-white">
                {item.biomarker.value}
              </span>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="h-[5px] w-[5px] flex-shrink-0 rounded-full bg-[#FFD000]" />
                <span className="type-label-sm font-medium text-white/80">
                  {item.biomarker.status}
                </span>
              </div>
            </>
          ) : item.biomarker.type === "pill" ? (
            <>
              <span className="block text-lg font-medium whitespace-nowrap text-white">
                {item.biomarker.value}
                <span className="type-label-sm ml-1 align-baseline font-normal text-white/70 normal-case">
                  {item.biomarker.unit}
                </span>
              </span>
              {/* `whitespace-nowrap` matches the value span above: these are
                  facsimile report pills, and a pill that wraps stops reading
                  as one. It also keeps them single-line now that German
                  hyphenation applies site-wide (AHD-10495) — a soft hyphen
                  lowers a word's min-content width, so
                  "Stoffwechsel­gesundheit" would otherwise break across
                  three lines inside the card. */}
              <div className="mt-1 flex items-center gap-1">
                <span className="type-label-sm rounded-full border border-white/[0.17] px-2 py-0.5 font-medium whitespace-nowrap text-white">
                  {item.biomarker.range}
                </span>
                <span className="type-label-sm rounded-full border border-white/[0.17] bg-white/[0.24] px-2 py-0.5 font-medium whitespace-nowrap text-white">
                  {item.biomarker.status}
                </span>
              </div>
            </>
          ) : (
            <span className="block text-lg font-medium whitespace-nowrap text-neutral-900">
              {item.biomarker.amount}
            </span>
          )}
        </div>
      )}

      {/* Learn more bar — hidden by default, revealed on hover */}
      <div className="absolute right-0 bottom-0 left-0 flex items-center justify-between px-5 py-3 opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100 md:px-6">
        <span className="text-base font-medium text-neutral-900/90">
          {learnMoreLabel || t("learnMore")}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className="text-neutral-900/90"
        >
          <path
            d="M3 8h10M9 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </Link>
  )
}

export const ServicesCarousel = memo(function ServicesCarousel({
  items,
  className,
  learnMoreLabel,
  disclaimerLabel,
  toolbarLeading,
}: ServicesCarouselProps) {
  const t = useTranslations("ui")
  const scrollRef = useRef<HTMLDivElement>(null)
  const [visibleSlide, setVisibleSlide] = useState(0)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)
  // The slide the chevrons are stepping toward. Kept separate from the live
  // scrollLeft so a burst of taps during the smooth-scroll animation
  // accumulates (1→2→3) instead of every tap re-reading an intermediate,
  // not-yet-settled scrollLeft and re-targeting the same card.
  const targetIndexRef = useRef(0)
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updateCarouselState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { scrollLeft, clientWidth, scrollWidth } = el
    // `|| 360`, not `?? 360`: a 0-width measurement (pre-layout / jsdom) is
    // invalid, so fall back to the default rather than producing a 16px step.
    const step =
      (el.firstElementChild?.getBoundingClientRect().width || 360) + 16
    setVisibleSlide(Math.min(Math.round(scrollLeft / step), items.length - 1))
    setCanScrollLeft(scrollLeft > 1)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1)
    // Re-sync the tap target to the real position, but only once scrolling has
    // settled. Recomputing it on every scroll tick would let a mid-animation
    // tap snap the target back to the current card — the bug that froze the
    // chevrons "after the third image". Debouncing lets bursts of taps land.
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current)
    settleTimerRef.current = setTimeout(() => {
      const node = scrollRef.current
      if (!node) return
      const s =
        (node.firstElementChild?.getBoundingClientRect().width || 360) + 16
      targetIndexRef.current = Math.round(node.scrollLeft / s)
    }, 150)
  }, [items.length])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateCarouselState()
    el.addEventListener("scroll", updateCarouselState, { passive: true })
    const ro = new ResizeObserver(updateCarouselState)
    ro.observe(el)
    return () => {
      el.removeEventListener("scroll", updateCarouselState)
      ro.disconnect()
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current)
    }
  }, [updateCarouselState])

  const scroll = (dir: "left" | "right") => {
    track.carouselScroll("services", dir)
    const el = scrollRef.current
    if (!el) return
    // Snap to the next/previous card boundary. Measure the first card's
    // width at runtime so mobile (300px) and md+ (344px) both land on a
    // real card boundary — a fixed 360px step overshoots on mobile.
    const card = el.firstElementChild as HTMLElement | null
    const step = (card?.getBoundingClientRect().width || 344) + 16 // gap-4 = 16px
    const maxLeft = el.scrollWidth - el.clientWidth
    const maxIndex = Math.max(0, Math.ceil(maxLeft / step))
    // Step from the last *committed* target, not the live scrollLeft, so taps
    // fired before the previous smooth scroll finishes still advance.
    const nextIndex = Math.min(
      Math.max(targetIndexRef.current + (dir === "right" ? 1 : -1), 0),
      maxIndex
    )
    targetIndexRef.current = nextIndex
    el.scrollTo({
      left: Math.min(nextIndex * step, maxLeft),
      behavior: "smooth",
    })
  }

  return (
    <div
      className={cn("w-full overflow-x-clip", className)}
      aria-roledescription="carousel"
    >
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {items[visibleSlide]?.title},{" "}
        {t("carouselPosition", {
          current: String(visibleSlide + 1),
          total: String(items.length),
        })}
      </div>
      {/* Carousel scroll container — padding is inside the scroll area so cards can scroll past it to the viewport edge.
          `pan-x pan-y` lets horizontal swipes drive the carousel AND vertical
          swipes fall through to the page scroll; `pan-x` alone swallowed
          vertical gestures started on a card. */}
      <div
        ref={scrollRef}
        className="scrollbar-hide flex gap-4 overflow-x-auto overscroll-x-contain pr-4 pb-2 pl-4 md:pr-10 md:pl-[max(2.5rem,calc((100vw-1440px)/2+2.5rem))]"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          touchAction: "pan-x pan-y",
        }}
      >
        {items.map((item, i) => (
          <ServiceCard key={i} item={item} learnMoreLabel={learnMoreLabel} />
        ))}
      </div>

      {/* Bottom row: 3-col grid so an optional toolbarLeading slot can sit
          centered while disclaimer (if any) hugs the left and the canonical
          DS chevron stepper hugs the right. The chevron stepper hides when
          all cards fit in view (mirrors the testimonials carousel + the
          membership cadence picker behavior). */}
      {(() => {
        const fitsInView = !canScrollLeft && !canScrollRight
        return (
          <div className="mx-auto max-w-[1440px] px-4 md:px-10">
            <div className="mt-4 grid grid-cols-3 items-center gap-4">
              <div className="justify-self-start">
                {disclaimerLabel ? (
                  <p className="text-sm text-neutral-400">{disclaimerLabel}</p>
                ) : null}
              </div>
              <div className="justify-self-center">{toolbarLeading}</div>
              {fitsInView ? (
                <span className="justify-self-end" />
              ) : (
                <CarouselNav
                  className="justify-self-end"
                  onPrev={() => scroll("left")}
                  onNext={() => scroll("right")}
                  atStart={!canScrollLeft}
                  atEnd={!canScrollRight}
                  prevLabel={t("scrollLeft")}
                  nextLabel={t("scrollRight")}
                />
              )}
            </div>
          </div>
        )
      })()}
    </div>
  )
})
